import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import classNames from 'classnames';

import Button from '../../../core/components/button/button';
import { ButtonType } from '../../../core/components/button/button-enums';
import Loader from '../../../core/components/loader/loader';
import { useIsMobile } from '../../../core/hooks/use-is-mobile';
import { useLocale } from '../../../core/hooks/use-locale';
import { useReportInitialLoad } from '../../../core/hooks/use-report-initial-load';
import {
  batchedUrlUpdates,
  useUrlParam,
} from '../../../core/hooks/use-url-param';
import { AppComponent } from '../../../core/types/core-types';
import { Icon, iconAfter } from '../../../core/utils/icons';
import { API_ROUTE_BASE } from '../../../core/utils/variables';
import FilterBadges from '../../components/filter/filter-badges';
import Input from '../../components/form-item/input';
import Select from '../../components/form-item/select';

import {
  fetchEmployeeDepartments,
  fetchEmployeeDivisions,
  getEmployees,
} from './employee-api';
import { Employee, EmployeeOption } from './employee-types';

const EmployeeList: AppComponent<{
  limit?: number;
  exportBtn?: boolean;
  termDescription?: string;
  divDescription?: string;
  depDescription?: string;
}> = ({
  InnerHTML,
  limit,
  exportBtn,
  termDescription,
  divDescription,
  depDescription,
}) => {
  const { Message } = useLocale();
  const isMobile = useIsMobile();

  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState<Employee[]>();
  const [divisions, setDivisions] = useState<EmployeeOption[]>();
  const [departments, setDepartments] = useState<EmployeeOption[]>([]);

  const [query, setQuery] = useUrlParam('string', 'query');
  const [selectedDiv, setSelectedDiv] = useUrlParam('object', 'di');
  const [selectedDep, setSelectedDep] = useUrlParam('object', 'de');

  const reportInitialLoad = useReportInitialLoad();

  useEffect(() => {
    const load = async () => {
      try {
        const employeeDivisionsData = await fetchEmployeeDivisions();

        unstable_batchedUpdates(() => {
          setDivisions(employeeDivisionsData);
        });
      } catch (e) {
        console.error(e);
        unstable_batchedUpdates(() => {
          setDivisions([]);
          setDepartments([]);
          setLoading(false);
        });
      }
    };

    load();
  }, []);

  useEffect(() => {
    const loadDepartmets = async () => {
      if (selectedDiv?.value !== null) {
        try {
          const employeeDepartmetsData = await fetchEmployeeDepartments({
            divisionId: parseInt(selectedDiv?.value ?? ''),
          });

          unstable_batchedUpdates(() => {
            setDepartments(employeeDepartmetsData);
            setSelectedDep(undefined);
          });
        } catch (e) {
          console.error(e);
          unstable_batchedUpdates(() => {
            setDivisions([]);
            setDepartments([]);
            setLoading(false);
          });
        }
      }
    };

    loadDepartmets();
  }, [selectedDiv?.value]);

  useEffect(() => {
    const getEmployeesList = async () => {
      try {
        setLoading(true);

        const result = await getEmployees({
          ...(query ? { search: query } : {}),
          ...(limit ? { limit } : {}),
          ...(selectedDiv?.value ? { divisions: [selectedDiv.value] } : {}),
          ...(selectedDep?.value ? { departments: [selectedDep.value] } : {}),
        });

        reportInitialLoad();

        unstable_batchedUpdates(() => {
          setEmployees(result);
          setLoading(false);
        });
      } catch (e) {
        console.error(e);
        unstable_batchedUpdates(() => {
          setEmployees([]);
          setLoading(false);
        });
      }
    };

    getEmployeesList();
  }, [limit, query, reportInitialLoad, selectedDep?.value, selectedDiv?.value]);

  return employees !== undefined ? (
    <>
      {loading && <Loader />}
      <div className="employee-list">
        <div className="employee-list__filter">
          <Input
            type="text"
            label={Message.SEARCH}
            value={query || ''}
            placeholder={Message.ENTER_SEARCH_TERM}
            onChange={(value) => {
              batchedUrlUpdates([[setQuery, value || undefined]]);
            }}
            tooltip={termDescription}
            isNarrow
            isUpdatedMessage={true}
          />
          {!!divisions && (
            <>
              <Select<EmployeeOption>
                label={Message.DIVISION}
                placeholder={Message.CHOOSE}
                value={(selectedDiv ?? null) as EmployeeOption}
                onChange={(value) => setSelectedDiv(value ?? undefined)}
                isClearable
                options={divisions}
                tooltip={divDescription}
                isUpdatedMessage={true}
              />
              <div className="employee-list__filter-department">
                <Select<EmployeeOption>
                  label={Message.DEPARTMENT}
                  placeholder={Message.CHOOSE}
                  value={(selectedDep ?? null) as EmployeeOption}
                  onChange={(value) => setSelectedDep(value ?? undefined)}
                  isClearable
                  options={departments}
                  tooltip={depDescription}
                  isUpdatedMessage={true}
                  {...(!!(departments.length === 0) && { isDisabled: true })}
                />
              </div>
            </>
          )}
          {!!exportBtn && (
            <Button
              href={`/wp-json${API_ROUTE_BASE}/employee/export`}
              buttonType={ButtonType.PRIMARY}
              className={iconAfter(Icon.PSK_DOWNLOAD)}
            >
              {Message.EXPORT_XLSX}
            </Button>
          )}
          <FilterBadges
            filters={[
              {
                label: Message.SEARCH_TERM,
                value: query,
                deactivate: () => setQuery(undefined),
              },
              {
                label: Message.DIVISION,
                value: selectedDiv?.label,
                deactivate: () => {
                  setSelectedDiv(undefined);
                  setSelectedDep(undefined);
                },
              },
              {
                label: Message.DEPARTMENT,
                value: selectedDep?.label,
                deactivate: () => setSelectedDep(undefined),
              },
            ]}
          />
        </div>
        {employees?.length > 0 ? (
          <div
            className="employee-list__wrapper"
            role="region"
            aria-live="polite"
            aria-atomic="true"
          >
            {employees.map(
              ({
                id,
                title_before,
                first_name,
                last_name,
                title_after,
                function: fun,
                office,
                phonenumber1,
                phonenumber2,
                email,
                division,
                department,
              }) => (
                <div className="employee-list__item" key={id}>
                  <h3 className="employee-list__name">
                    {!!title_before && (
                      <span className="employee-list__title-before">
                        {title_before}
                      </span>
                    )}
                    <div className="employee-list__name-part">
                      <span>{first_name}</span>
                      <span>{last_name}</span>
                    </div>
                    {!!title_after && (
                      <span className="employee-list__title-after">
                        {title_after}
                      </span>
                    )}
                  </h3>
                  {!isMobile ? (
                    <p
                      className={classNames(
                        'employee-list__position',
                        !!fun && 'nomargin'
                      )}
                    >
                      {division?.name}
                      {!!department && ', '}
                      {department?.name}
                    </p>
                  ) : (
                    <div
                      className={classNames(
                        'employee-list__position',
                        'flex flex--column',
                        !!fun && 'nomargin'
                      )}
                    >
                      <p className="nomargin">{division?.name}</p>
                      <p className="nomargin">{department?.name}</p>
                    </div>
                  )}
                  {!!fun && <p className="employee-list__function">{fun}</p>}
                  {!!email && (
                    <span>
                      <a href={`mailto:${email}`}>{email}</a>
                    </span>
                  )}
                  {!!(phonenumber1 || phonenumber2) && (
                    <div className="employee-list__numbers">
                      {!!phonenumber1 && (
                        <a href={`tel:${phonenumber1}`}>{phonenumber1}</a>
                      )}
                      {!!phonenumber2 && (
                        <a href={`tel:${phonenumber2}`}>{phonenumber2}</a>
                      )}
                    </div>
                  )}
                  <p>Miestnosť: {office}</p>
                </div>
              )
            )}
          </div>
        ) : (
          <div className="employee-list__wrapper">
            <h2 className="has-black-color">{Message.NO_OPTIONS}</h2>
          </div>
        )}
      </div>
    </>
  ) : (
    <InnerHTML />
  );
};

export default EmployeeList;
